import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AmenitiesPage() {
  const [amenities, setAmenities] = useState([]);
  const [upcomingAmenities, setUpcomingAmenities] = useState([]);

  // Helper function to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api/images/') 
      ? `${process.env.REACT_APP_BACKEND_URL}${url}/data` 
      : url;
  };

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const response = await axios.get(`${API}/amenities`);
        if (response.data.length === 0) {
          await axios.post(`${API}/seed-amenities`);
          const newResponse = await axios.get(`${API}/amenities`);
          setAmenities(newResponse.data.filter(a => !a.is_upcoming));
          setUpcomingAmenities(newResponse.data.filter(a => a.is_upcoming));
        } else {
          setAmenities(response.data.filter(a => !a.is_upcoming));
          setUpcomingAmenities(response.data.filter(a => a.is_upcoming));
        }
      } catch (error) {
        console.error('Error fetching amenities:', error);
      }
    };
    fetchAmenities();
  }, []);

  useEffect(() => {
    document.title = 'Club JCI Ltd - Amenities';
  }, []);

  return (
    <div data-testid="amenities-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="amenities-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Amenities</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#F1F5F9] mb-6">
              World-Class <span className="gold-text">Facilities</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Discover the exceptional amenities that make Club JCI the premier destination 
              for discerning professionals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Current Amenities */}
      <section className="section-padding bg-[#0A192F]" data-testid="current-amenities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {amenities.map((amenity, index) => (
              <motion.div
                key={amenity.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`amenity-${amenity.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="aspect-[4/3] rounded-sm overflow-hidden">
                    <img
                      src={getImageUrl(amenity.image_url)}
                      alt={amenity.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="text-3xl font-semibold text-[#F1F5F9] mb-4">{amenity.name}</h2>
                  <p className="text-[#94A3B8] leading-relaxed">{amenity.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Amenities */}
      {upcomingAmenities.length > 0 && (
        <section className="section-padding" data-testid="upcoming-amenities">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] mb-4">
                Coming <span className="gold-text">Soon</span>
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                Exciting new amenities are on the horizon to enhance your club experience.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingAmenities.map((amenity, index) => (
                <motion.div
                  key={amenity.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-card rounded-sm overflow-hidden"
                  data-testid={`upcoming-${amenity.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="h-48 overflow-hidden relative">
                    <img
                      src={getImageUrl(amenity.image_url)}
                      alt={amenity.name}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="upcoming-badge">Coming Soon</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-[#F1F5F9] mb-2">{amenity.name}</h3>
                    <p className="text-sm text-[#94A3B8]">{amenity.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
